import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { Box, Button, Group, Stack, Text, Title } from '@grovus/design-system'
import { DICTIONARY_COPY, type Lang } from '../i18n'
import { fill, money } from './format'
import { labelOf, type ViewContext } from './view'
import { registerOverlay, unregisterOverlay, OVERLAY_LAYER } from '../components/overlayRegistry'
import type { MergeKeep } from './api'
import type { Row } from './partition'

const BORDER = '1px solid var(--mantine-color-default-border)'
const OVERLAY_ID = 'merge-dialog'

interface Props {
  lang: Lang
  ctx: ViewContext
  /** The creatives to merge, in the table's order. */
  rows: Row[]
  /** Their distinct assignments; two or more ask the reader to choose one. */
  assignments: MergeKeep[]
  /** The line a refused or failed merge left, shown until the next attempt. */
  line: string | null
  saving: boolean
  onConfirm: (keep: MergeKeep | null) => void
  onCancel: () => void
}

const keyOf = (keep: MergeKeep) => `${keep.hypothesisId}\u0000${keep.version ?? ''}`

/**
 * The merge confirmation (GRO-161). The design system exports no modal, so it is drawn as
 * the dictionary screen's dialog is: a backdrop over the page, focus taken inside on open
 * and held there, Escape or Cancel to close, focus returned to what opened it.
 *
 * The detail panel may be open beneath it and listens for Tab and Escape on the document.
 * The screen suspends the panel while this is open, the dialog registers above it on the
 * overlay stack, and both keys stop here, so neither reaches the panel.
 */
export function MergeDialog({ lang, ctx, rows, assignments, line, saving, onConfirm, onCancel }: Props) {
  const copy = DICTIONARY_COPY[lang]
  const titleId = useId()
  const panel = useRef<HTMLDivElement>(null)
  const [chosen, setChosen] = useState<string | null>(null)
  const choosing = assignments.length > 1
  // A choice that is no longer among the assignments, after a refetch, is no choice.
  const keep = choosing ? assignments.find((candidate) => keyOf(candidate) === chosen) ?? null : assignments[0] ?? null
  const ready = !saving && (!choosing || keep !== null)

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    registerOverlay(OVERLAY_ID, OVERLAY_LAYER.dialog)
    panel.current?.querySelector<HTMLElement>('input, button')?.focus()
    return () => {
      unregisterOverlay(OVERLAY_ID)
      if (opener?.isConnected) opener.focus()
    }
  }, [])

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      event.preventDefault()
      if (!saving) onCancel()
      return
    }
    if (event.key !== 'Tab' || panel.current === null) return
    event.stopPropagation()
    event.preventDefault()
    const focusable = [...panel.current.querySelectorAll<HTMLElement>('input, button')].filter(
      (element) => !element.hasAttribute('disabled'),
    )
    // A radio group is one tab stop, as the browser has it: the checked radio, or the
    // first when none is checked.
    const stopOf = (element: Element | null): Element | null => {
      if (!(element instanceof HTMLInputElement) || element.type !== 'radio') return element
      const group = focusable.filter(
        (other) => other instanceof HTMLInputElement && other.type === 'radio' && other.name === element.name,
      ) as HTMLInputElement[]
      return group.find((radio) => radio.checked) ?? group[0]
    }
    const stops = focusable.filter((element) => stopOf(element) === element)
    if (stops.length === 0) return
    const at = stops.indexOf(stopOf(document.activeElement) as HTMLElement)
    const next = event.shiftKey
      ? at <= 0 ? stops.length - 1 : at - 1
      : at < 0 || at === stops.length - 1 ? 0 : at + 1
    stops[next].focus()
  }

  const label = (keep: MergeKeep) => {
    const name = labelOf(ctx.dimensions.hypotheses, keep.hypothesisId, lang) ?? keep.hypothesisId
    return keep.version === null ? name : `${name} · ${keep.version}`
  }

  return (
    <Box
      style={{
        position: 'fixed', inset: 0, zIndex: OVERLAY_LAYER.dialog, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 16, background: 'rgba(0, 0, 0, 0.45)',
      }}
    >
      <Box
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        data-testid="merge-dialog"
        p={20}
        style={{
          width: 480, maxWidth: '100%', maxHeight: '100%', overflowY: 'auto',
          background: 'var(--mantine-color-body)', border: BORDER, borderRadius: 'var(--mantine-radius-lg)',
        }}
      >
        <Stack gap={12}>
          <Title order={3} fz="md" id={titleId}>{fill(copy.mergeTitle, { c: rows.length })}</Title>
          <Stack gap={4} component="ul" style={{ margin: 0, paddingLeft: 18 }}>
            {rows.map((row) => (
              <Text key={row.id} size="sm" component="li">
                {row.displayName} · {money(row.metrics.spend, ctx.currency, lang)}
              </Text>
            ))}
          </Stack>
          <Text size="sm">{copy.mergeBody}</Text>
          {choosing ? (
            <Stack gap={6} component="fieldset" style={{ border: 0, margin: 0, padding: 0 }}>
              <Text size="sm" component="legend" fw={500}>{copy.mergeChoose}</Text>
              {assignments.map((candidate) => {
                const key = keyOf(candidate)
                return (
                  <label key={key} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                    <input
                      type="radio"
                      name="merge-keep"
                      value={key}
                      checked={chosen === key}
                      onChange={() => setChosen(key)}
                      disabled={saving}
                      data-testid="merge-keep"
                    />
                    {label(candidate)}
                  </label>
                )
              })}
            </Stack>
          ) : (
            <Text size="sm" data-testid="merge-keeps">
              {fill(copy.mergeKeeps, { h: keep === null ? copy.mergeNone : label(keep) })}
            </Text>
          )}
          {line !== null && <Text size="sm" role="alert">{line}</Text>}
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={onCancel} disabled={saving}>{copy.cancel}</Button>
            <Button onClick={() => onConfirm(keep)} disabled={!ready} loading={saving} data-testid="merge-confirm">
              {copy.mergeConfirm}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Box>
  )
}
